import { useMemo } from 'react';

import type { ReferencesContext_AddressableConfig } from '../../../contexts/ReferencesContext';

import { AddressFormTab } from '../enums/tab.enum';
import { AddressGeneralInfoFormStep } from '../steps/GeneralInfo';
import { AddressInterativeMapFormStep } from '../steps/InteractiveMap';
import { AddressPostalInfoFormStep } from '../steps/PostalInfo';
import { AddressLocationInfoFormStep } from '../steps/LocationInfo';
import { AddressCadastralInfoFormStep } from '../steps/CadastralInfo';
import { GeneralInfoSchema } from '../schemas/general-info.schema';

interface Params {
  readonly config: ReferencesContext_AddressableConfig;
  readonly enabledSteps: readonly AddressFormTab[];
  readonly stepIdsSubset?: readonly AddressFormTab[];
  readonly setEnabledSteps: (enabledSteps: readonly AddressFormTab[]) => void;
}

export const useAddressSteps = ({
  config,
  enabledSteps,
  stepIdsSubset,
  setEnabledSteps,
}: Readonly<Params>) => {
  const { steps, wizardChildren } = useMemo(() => {
    const addressInteractiveMap = {
      step: {
        id: AddressFormTab.InteractiveMap,
        title: 'Carte Interactive',
        isEnabled: true,
      },
      child: (
        <AddressInterativeMapFormStep
          config={config}
          id={AddressFormTab.InteractiveMap}
          key={AddressFormTab.InteractiveMap}
        />
      ),
    };

    const addressGeneralInfo = {
      step: {
        id: AddressFormTab.GeneralInfo,
        title: 'Informations Générales',
        isEnabled: true,
        ValidationSchema: GeneralInfoSchema,
      },
      child: (
        <AddressGeneralInfoFormStep
          config={config}
          id={AddressFormTab.GeneralInfo}
          key={AddressFormTab.GeneralInfo}
          setEnabledSteps={setEnabledSteps}
        />
      ),
    };

    const addressPostalInfo = {
      step: {
        id: AddressFormTab.PostalInfo,
        title: 'Informations Postales',
        isEnabled: enabledSteps.includes(AddressFormTab.PostalInfo),
      },
      child: (
        <AddressPostalInfoFormStep
          id={AddressFormTab.PostalInfo}
          key={AddressFormTab.PostalInfo}
        />
      ),
    };

    const addressLocationInfo = {
      step: {
        id: AddressFormTab.LocationInfo,
        title: 'Informations de Localisation',
        isEnabled: enabledSteps.includes(AddressFormTab.LocationInfo),
      },
      child: (
        <AddressLocationInfoFormStep
          id={AddressFormTab.LocationInfo}
          key={AddressFormTab.LocationInfo}
        />
      ),
    };

    const addressCadastralInfo = {
      step: {
        id: AddressFormTab.CadastralInfo,
        title: 'Informations Cadastrales',
        isEnabled: enabledSteps.includes(AddressFormTab.CadastralInfo),
      },
      child: (
        <AddressCadastralInfoFormStep
          id={AddressFormTab.CadastralInfo}
          key={AddressFormTab.CadastralInfo}
        />
      ),
    };

    const allTabs = [
      [AddressFormTab.InteractiveMap, addressInteractiveMap],
      [AddressFormTab.GeneralInfo, addressGeneralInfo],
      [AddressFormTab.PostalInfo, addressPostalInfo],
      [AddressFormTab.LocationInfo, addressLocationInfo],
      [AddressFormTab.CadastralInfo, addressCadastralInfo],
    ] as const;

    const filtered = !stepIdsSubset?.length
      ? allTabs
      : allTabs.filter(([tab]) => stepIdsSubset.includes(tab));

    const steps = filtered.map(([, { step }]) => step);
    const wizardChildren = filtered.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [config, enabledSteps, setEnabledSteps, stepIdsSubset]);

  return { steps, wizardChildren };
};
